import React, { useState, useEffect } from 'react';
import { Sun, Moon, Star, Calendar, Clock, Sparkles, Share2 } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa6';
import api from '../utils/api';

const DailyPanchangWidget = ({ compact = false }) => {
  const [panchang, setPanchang] = useState(null);
  const [horoscope, setHoroscope] = useState(null);
  const [selectedSign, setSelectedSign] = useState('aries');
  const [loading, setLoading] = useState(true);
  const [signs, setSigns] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedSign) {
      fetchHoroscope(selectedSign);
    }
  }, [selectedSign]);

  const fetchData = async () => {
    try {
      const [panchangRes, signsRes] = await Promise.all([
        api.get('/sankat-mochan/panchang/today'),
        api.get('/sankat-mochan/horoscope/signs')
      ]);
      
      if (panchangRes.data?.success) {
        setPanchang(panchangRes.data.panchang);
      }
      if (signsRes.data?.success) {
        setSigns(signsRes.data.signs);
      }
    } catch (error) {
      console.error('Error fetching panchang:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHoroscope = async (sign) => {
    try {
      const res = await api.get(`/sankat-mochan/horoscope/${sign}`);
      if (res.data?.success) {
        setHoroscope(res.data.horoscope);
      }
    } catch (error) {
      console.error('Error fetching horoscope:', error);
    }
  };

  const shareOnWhatsApp = () => {
    const text = `🌟 Daily Panchang - ${panchang?.date_hindi || 'Today'}

📅 ${panchang?.weekday_hindi} (${panchang?.weekday})
🙏 Deity: ${panchang?.deity_of_day}
🎨 Lucky Color: ${panchang?.lucky_color}

✨ Tithi: ${panchang?.tithi} (${panchang?.paksha} Paksha)
⭐ Nakshatra: ${panchang?.nakshatra}
🔮 Yoga: ${panchang?.yoga}

🌅 Sunrise: ${panchang?.sunrise}
🌇 Sunset: ${panchang?.sunset}
⚠️ Rahu Kaal: ${panchang?.rahu_kaal}

${horoscope ? `\n♈ ${horoscope.sign_name} Horoscope:\n${horoscope.horoscope}` : ''}

📱 Get daily spiritual guidance at Anant Web Portal!`;
    
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-2xl p-6">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-white/10 rounded w-full"></div>
          <div className="h-4 bg-white/10 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="bg-gradient-to-br from-purple-900/40 to-indigo-900/40 rounded-xl p-4 border border-purple-500/20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-yellow-400" />
            <span className="font-semibold text-sm">{panchang?.weekday_hindi}</span>
          </div>
          <button 
            onClick={shareOnWhatsApp}
            className="p-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-colors"
          >
            <FaWhatsapp className="w-4 h-4 text-green-400" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5">
            <Moon className="w-3 h-3 text-blue-400" />
            <span className="text-gray-300">{panchang?.tithi}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Star className="w-3 h-3 text-yellow-400" />
            <span className="text-gray-300">{panchang?.nakshatra}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-900/40 via-indigo-900/30 to-violet-900/40 rounded-2xl border border-purple-500/20 overflow-hidden" data-testid="panchang-widget">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600/30 to-indigo-600/30 p-4 border-b border-purple-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-yellow-500/20">
              <Sun className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Daily Panchang</h3>
              <p className="text-sm text-gray-400">{panchang?.weekday_hindi} • {panchang?.date_hindi}</p>
            </div>
          </div>
          <button 
            onClick={shareOnWhatsApp}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-500/20 hover:bg-green-500/30 transition-colors group"
            data-testid="whatsapp-share-btn"
          >
            <FaWhatsapp className="w-5 h-5 text-green-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-green-400 hidden sm:inline">Share</span>
          </button>
        </div>
      </div>

      {/* Special Day Banner */}
      {panchang?.special_day && (
        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-4 py-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-medium text-yellow-300">
            {panchang.special_day.name_hi} ({panchang.special_day.name})
          </span>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4 space-y-4">
        {/* Panchang Details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Moon className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-gray-400">Tithi</span>
            </div>
            <p className="font-semibold">{panchang?.tithi}</p>
            <p className="text-xs text-gray-500">{panchang?.paksha} Paksha</p>
          </div>
          
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-xs text-gray-400">Nakshatra</span>
            </div>
            <p className="font-semibold">{panchang?.nakshatra}</p>
          </div>
          
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-gray-400">Yoga</span>
            </div>
            <p className="font-semibold">{panchang?.yoga}</p>
          </div>
          
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-green-400" />
              <span className="text-xs text-gray-400">Karana</span>
            </div>
            <p className="font-semibold">{panchang?.karana}</p>
          </div>
        </div>

        {/* Time Info */}
        <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-orange-400" />
            <div>
              <p className="text-xs text-gray-400">Sunrise / Sunset</p>
              <p className="text-sm font-medium">{panchang?.sunrise} - {panchang?.sunset}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-red-400">Rahu Kaal</p>
            <p className="text-sm font-medium text-red-300">{panchang?.rahu_kaal}</p>
          </div>
        </div>

        {/* Daily Deity */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400">Today's Deity</p>
            <p className="font-semibold text-amber-300">{panchang?.deity_of_day}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Lucky Color</p>
            <p className="font-semibold">{panchang?.lucky_color}</p>
          </div>
        </div>

        {/* Horoscope Section */}
        <div className="border-t border-purple-500/20 pt-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-yellow-400" />
            Daily Horoscope
          </h4>
          
          {/* Zodiac Sign Selector */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {signs.map((sign) => (
              <button
                key={sign.id}
                onClick={() => setSelectedSign(sign.id)}
                className={`px-2 py-1 rounded-lg text-xs transition-colors ${
                  selectedSign === sign.id
                    ? 'bg-purple-500/40 text-purple-200'
                    : 'bg-white/5 hover:bg-white/10 text-gray-400'
                }`}
                title={`${sign.name_en} (${sign.name_hi})`}
              >
                {sign.symbol}
              </button>
            ))}
          </div>

          {/* Horoscope Content */}
          {horoscope && (
            <div className="bg-white/5 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{horoscope.symbol}</span>
                <div>
                  <p className="font-semibold">{horoscope.sign_name}</p>
                  <p className="text-xs text-gray-400">{horoscope.dates}</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{horoscope.horoscope}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyPanchangWidget;
